import React, { useState, useMemo, useRef } from "react";
import {
  Card,
  Button,
  Select,
  Row,
  Col,
  Divider,
  Upload,
  Space,
  Progress,
  Grid,
  Tooltip,
  Transfer,
} from "antd";
import {
  UploadOutlined,
  FolderOpenOutlined,
  DownloadOutlined,
  SyncOutlined,
  ApiOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { processFileThunk, resetAll } from "../../store/slices/fileSlice";
import {
  setJoinKeys,
  setJoinType,
  setSelectedColumns1,
  setSelectedColumns2,
  resetAnalysis,
  uploadAndAnalyzeThunk,
  mergeFilesThunk,
  setFile1,
  setFile2,
  setUploadProgress,
  setActiveTab,
  setAppendMapping,
  setMergedFileUrl,
  setBlobFilePath,
  setSmartMergeMaxFileSize
} from "../../store/slices/smartMergeSlice";
import { Cable } from "lucide-react";
import "../DataHistory/ColumnSelectionCard.css";
import "./SmartMergeDashboard.css"
import { formatBytes } from "../../Utils/helperMethod";

const { Option } = Select;
const { useBreakpoint } = Grid;


const joinTypesInfo = [
  {
    technical: "Inner",
    functional: "Common Records",
    logic: "Returns only the rows where there is a match in both tables.",
    example:
      "Audit: Listing only the Purchase Orders that have been specifically Goods Receipted.",
  },
  {
    technical: "Left",
    functional: "Master - Base join",
    logic:
      "Keeps all rows from the first table and adds matching info from the second.",
    example:
      "Employee Listing: A list of all staff, adding their office location if one is assigned.",
  },
  {
    technical: "Right",
    functional: "Target - Base join",
    logic:
      "Keeps all rows from the second table and adds matching info from the first.",
    example:
      "System Audit: A list of all server logs, adding user names only for known accounts.",
  },
  {
    technical: "Outer",
    functional: "Total consolidation",
    logic: "Combines every row from both tables, regardless of a match.",
    example:
      "Reconciliation: Merging two different bank statements to find all unique entries.",
  },
];

const SmartMergeDashboard = () => {
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    file1,
    file2,
    uploadProgress,
    activeTab,
    appendMapping,
    fileUrl1,
    fileUrl2,
    file1Columns,
    file2Columns,
    selectedColumns1,
    selectedColumns2,
    joinKeys,
    joinType,
    mergedFileUrl,
    blobFilePath,
    uploading,
    merging,
    analysisComplete,
    smartMergeMaxFileSize,
  } = useSelector((state) => state.smartMerge);
  const planMaxUploadFileSize = useSelector((state) => state.auth.maxUploadFileSize);

  const abortControllerRef = useRef(null);

  const handleResetAnalysis = () => {
    dispatch(resetAnalysis());
    dispatch(setMergedFileUrl(null));
    dispatch(setBlobFilePath(null));
    dispatch(setSmartMergeMaxFileSize(null));
  };

  const handleFile1Change = (info) => {
    // We only take the first file
    dispatch(setFile1(info.file));
    // Reset downstream state if file changes
    handleResetAnalysis();
    return false; // Prevent antd's default upload
  };

  const handleFile2Change = (info) => {
    dispatch(setFile2(info.file));
    handleResetAnalysis();
    return false;
  };

  const handleRemoveFile1 = () => {
    dispatch(setFile1(null));
    dispatch(resetAll());
    handleResetAnalysis();
  };

  const handleRemoveFile2 = () => {
    dispatch(setFile2(null));
    dispatch(resetAll());
    handleResetAnalysis();
  };

  const handleUploadAndAnalyze = async () => {
    if (!file1 || !file2) {
      toast.error("Please select both files before uploading.", {
        theme: "colored",
      });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    dispatch(setUploadProgress(0));

    const promise = dispatch(
      uploadAndAnalyzeThunk({
        file1,
        file2,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            dispatch(setUploadProgress(percentCompleted));
          }
        },
      })
    );
    abortControllerRef.current = promise;

    try {
      const resultAction = await promise.unwrap();
      const cols1 = resultAction.file1.columns_metadata.map((c) => c.name);
      const cols2 = resultAction.file2.columns_metadata.map((c) => c.name);

      // Initialize Append Mapping (Auto-match by name)
      const autoMapping = cols1.map((col1) => {
        const match = cols2.find((c2) => c2.toLowerCase() === col1.toLowerCase());
        return { target: col1, source: match || null };
      });
      dispatch(setAppendMapping(autoMapping));

      // Explicitly set targetKeys to empty arrays so all items default to the "Source" list
      dispatch(setSelectedColumns1([]));
      dispatch(setSelectedColumns2([]));

      toast.success("Files uploaded successfully!", { theme: "colored" });
    } catch (error) {
      if (error?.name === "AbortError" || error?.message === "canceled" || error?.name === "CanceledError") {
        return;
      }
      console.error("File analysis failed:", error);
      toast.error(
        error || "File analysis failed. Please try again.",
        { theme: "colored" }
      );
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      toast.info("Upload cancelled.", { theme: "colored" });
    }
  };

  const handleJoinKeyChange = (index, key, value) => {
    const newKeys = joinKeys.map((pair, i) =>
      i === index ? { ...pair, [key]: value } : pair
    );
    dispatch(setJoinKeys(newKeys));
  };

  const addJoinKey = () => {
    dispatch(setJoinKeys([...joinKeys, { key1: null, key2: null }]));
  };

  const removeJoinKey = (index) => {
    const newKeys = joinKeys.filter((_, i) => i !== index);
    dispatch(setJoinKeys(newKeys));
  };

  const handleMerge = async () => {
    let payload = {};
    let endpoint = "";

    if (activeTab === "join") {
      const invalidKey = joinKeys.some((pair) => !pair.key1 || !pair.key2);
      if (invalidKey) {
        toast.error("Please define all join keys.", { theme: "colored" });
        return;
      }

      if (selectedColumns1.length === 0 || selectedColumns2.length === 0) {
        toast.error("Please select columns from both files.", { theme: "colored" });
        return;
      }

      endpoint = "/file_merging/merge-files";
      payload = {
        file1_blob_path: fileUrl1,
        file2_blob_path: fileUrl2,
        merge_type: joinType.toLowerCase(),
        select_columns_file1: selectedColumns1,
        select_columns_file2: selectedColumns2,
        join_keys_primary: joinKeys.map((k) => k.key1),
        join_keys_secondary: joinKeys.map((k) => k.key2),
      };
    } else {
      // Append Logic
      endpoint = "/file_merging/append-files";
      payload = {
        file1_blob_path: fileUrl1,
        file2_blob_path: fileUrl2,
        strict_schema: false,
      };
    }

    try {
      const response = await dispatch(
        mergeFilesThunk({ endpoint, payload }),
      ).unwrap();
      dispatch(setSmartMergeMaxFileSize(response?.merged_file?.file_size_mb || response?.appended_file?.file_size_mb));

      toast.success(
        "Files merged successfully! You can now download the result.",
        {
          theme: "colored",
        },
      );

      const extraColsCount =
        response?.merge_metadata?.extra_columns_count_in_final_merged ??
        response?.append_metadata?.extra_columns_count_in_final_append;

      if (extraColsCount !== undefined) {
        toast.info(`Extra columns in final merged file: ${extraColsCount}`, {
          theme: "colored",
        });
      }
    } catch (error) {
      toast.error(error || "Merging failed. Please check your configuration.", {
        theme: "colored",
      });
    }
  };

  const handleDownload = () => {
    if (mergedFileUrl) {
      window.open(mergedFileUrl, "_blank");
    } else {
      toast.warn(
        "No merged file to download. Please execute the data sync first.",
        { theme: "colored" }
      );
    }
  };


  const connect_result_file_to_analytics  = async () => {
    if (!blobFilePath) {
      toast.error("No merged file available to process.", { theme: "colored" });
      return;
    }

    try {
      await dispatch(
        processFileThunk({
          file_name: blobFilePath,
        })
      ).unwrap();

      navigate("/dashboard/anamoly-detection");
    } catch (err) {
      const errorMsg = typeof err === "string" ? err : err?.message || "Processing failed";
      toast.error(errorMsg, {
        duration: 10000,
        theme: "dark",
      });
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-8 text-white">
      {/* ================= HEADER ================= */}

      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-300 via-violet-400 to-purple-500 bg-clip-text text-transparent">
          Data Merge Studio
        </h1>

        <p className="text-white text-lg ">
          Upload File1 & File2 — select join keys — merge join — explore results
        </p>
      </div>

      {/* ================= FILE UPLOAD SECTION ================= */}

      <Card className="bg-[#020617] border border-gray-700 mb-12">
        <div className="mb-4 flex items-center gap-2 text-purple-300 font-semibold">
          <UploadOutlined />
          Upload Files
        </div>

        <p className="text-gray-400 mb-6">
          Select both Excel/CSV files to begin the merge process.
        </p>

        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={10}>
            <Upload
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleFile1Change}
              showUploadList={false}
            >
              <Button
                icon={<FolderOpenOutlined />}
                className="w-full bg-[#1c1a3a] border-purple-600 text-purple-200 hover:!bg-[#2a284a] hover:!border-purple-500"
              >
                Select File 1
              </Button>
            </Upload>
            {file1 && (
              <div className="flex items-center justify-evenly mt-2 text-gray-300">
                <div className="flex flex-col overflow-hidden mr-2">
                  <span className="truncate" title={file1.name}>
                    Selected Table 1: {file1.name}
                  </span>
                  <span className="text-sm text-gray-400">
                    Size: {formatBytes(file1.size)}
                  </span>
                </div>
                <Button
                  icon={<DeleteOutlined />}
                  type="link"
                  danger
                  size="small"
                  onClick={handleRemoveFile1}
                  aria-label="Remove file 1"
                />
              </div>
            )}
          </Col>

          <Col xs={24} lg={10}>
            <Upload
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleFile2Change}
              showUploadList={false}
            >
              <Button
                icon={<FolderOpenOutlined />}
                className="w-full bg-[#1c1a3a] border-purple-600 text-purple-200 hover:!bg-[#2a284a] hover:!border-purple-500"
              >
                Select File 2
              </Button>
            </Upload>
            {file2 && (
              <div className="flex items-center justify-evenly mt-2 text-gray-300">
                <div className="flex flex-col overflow-hidden mr-2">
                  <span className="truncate" title={file2.name}>
                    Selected Table 2: {file2.name}
                  </span>
                  <span className="text-sm text-gray-400">
                    Size: {formatBytes(file2.size)}
                  </span>
                </div>
                <Button
                  icon={<DeleteOutlined />}
                  type="link"
                  danger
                  size="small"
                  onClick={handleRemoveFile2}
                  aria-label="Remove file 2"
                />
              </div>
            )}
          </Col>
          <Col xs={24} lg={6}>
            <Button
              type="primary"
              icon={<ApiOutlined />}
              loading={uploading}
              // disabled={!file1 || !file2}
              hidden={!file1 || !file2}
              onClick={handleUploadAndAnalyze}
              className="w-full whitespace-nowrap"
            >
              Upload & Analyze
            </Button>
          </Col>
        </Row>
      </Card>

      {/* ================= DATA ARCHITECT ================= */}

      {uploading && (
        <div className="text-center px-8">
          <p className="text-lg text-purple-200 mb-2">
            {uploadProgress < 100
              ? `Uploading files... ${uploadProgress}%`
              : "Analyzing files and extracting columns..."}
          </p>
          <Progress
            percent={uploadProgress}
            status="active"
            strokeColor={{ from: "#87d068", to: "#108ee9" }}
            format={(percent) => <span style={{ color: "white" }}>{percent}%</span>}
          />
          <Button
            danger
            className="mt-4"
            onClick={handleCancelUpload}
            icon={<DeleteOutlined />}
          >
            Cancel Upload
          </Button>
        </div>
      )}

      {analysisComplete && (
        <>
          <h1 className="text-2xl font-bold text-gray-200 mb-6">
            XynexAI Data Architect
          </h1>

          {/* Tabs */}

          <div className="flex gap-2 mb-6">
            <Button
              className={`${
                activeTab === "join"
                  ? "bg-purple-600 !text-white hover:!bg-purple-700"
                  : "bg-[#0f172a] !text-gray-300 border-gray-700 hover:!bg-gray-800"
              } border-none transition-colors`}
        onClick={() => {
          dispatch(setActiveTab("join"));
          dispatch(setMergedFileUrl(null));
          dispatch(setBlobFilePath(null));
        }}
            >
              Join Features
            </Button>

            <Button
              className={`${
                activeTab === "append"
                  ? "bg-purple-600 !text-white hover:!bg-purple-700"
                  : "bg-[#0f172a] !text-gray-300 border-gray-700 hover:!bg-gray-800"
              } border-none transition-colors`}
        onClick={() => {
          dispatch(setActiveTab("append"));
          dispatch(setMergedFileUrl(null));
          dispatch(setBlobFilePath(null));
        }}
            >
              Append Features
            </Button>
          </div>

          <Divider className="border-gray-700" />

          {/* JOIN LOGIC */}

          {activeTab === "join" && (
            <>
              <div className="mb-8">
                <h3 className="text-purple-400 font-semibold mb-4">
                  1. SELECT JOIN LOGIC
                </h3>

                <Row gutter={16}>
                  {joinTypesInfo.map((type) => (
                    <Col xs={12} md={6} key={type.technical}>
                      <Tooltip
                        title={
                          <div className="p-1">
                            <p className="font-semibold">{type.logic}</p>
                            <p className="text-xs italic mt-1">
                              e.g., {type.example}
                            </p>
                          </div>
                        }
                      >
                        <Card
                          onClick={() => dispatch(setJoinType(type.technical))}
                          className={`bg-[#020617] border text-center cursor-pointer transition-all ${
                            joinType === type.technical
                              ? "border-purple-400 border-4"
                              : "border-gray-500 hover:border-purple-400"
                          }`}
                          styles={{ body: { padding: screens.xs ? 12 : 20 } }}
                        >
                          <div className="text-base sm:text-lg font-semibold text-white">
                            {type.functional}
                          </div>
                          {/* <div className="text-xs sm:text-sm text-gray-400 mt-1">
                            {type.technical} Join
                          </div> */}
                        </Card>
                      </Tooltip>
                    </Col>
                  ))}
                </Row>
              </div>

              <div className="mb-8">
                <h3 className="text-purple-400 font-semibold mb-4">
                  2. DEFINE JOIN KEYS
                </h3>

                <Card className="bg-[#020617] border border-gray-700">
                  <Space direction="vertical" className="w-full" size="middle">
                    {joinKeys.map((pair, index) => (
                      <Row gutter={[16, 8]} align="middle" key={index}>
                        <Col xs={24} lg={10}>
                          <Select
                            placeholder="Select Primary Key"
                            value={pair.key1}
                            onChange={(value) =>
                              handleJoinKeyChange(index, "key1", value)
                            }
                            className="w-full custom-select-dark"
                            popupClassName="custom-select-dropdown-dark"
                            showSearch
                            filterOption={(input, option) =>
                              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                          >
                            {file1Columns.map((col) => (
                              <Option key={col} value={col}>
                                {col}
                              </Option>
                            ))}
                          </Select>
                        </Col>

                        <Col
                          xs={24}
                          lg={2}
                          className="text-center text-lg text-white"
                        >
                          =
                        </Col>

                        <Col xs={24} lg={10}>
                          <Select
                            placeholder="Select Secondary Key"
                            value={pair.key2}
                            onChange={(value) =>
                              handleJoinKeyChange(index, "key2", value)
                            }
                            className="w-full custom-select-dark"
                            popupClassName="custom-select-dropdown-dark"
                            showSearch
                            filterOption={(input, option) =>
                              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                          >
                            {file2Columns.map((col) => (
                              <Option key={col} value={col}>
                                {col}
                              </Option>
                            ))}
                          </Select>
                        </Col>

                        <Col
                          xs={24}
                          lg={2}
                          className="flex justify-end lg:justify-center"
                        >
                          {joinKeys.length > 1 && (
                            <Button
                              danger
                              type="link"
                              icon={<DeleteOutlined />}
                              onClick={() => removeJoinKey(index)}
                            />
                          )}
                        </Col>
                      </Row>
                    ))}
                    <Button
                      type="dashed"
                      onClick={addJoinKey}
                      icon={<PlusOutlined />}
                      className="w-full lg:w-2/12"
                    >
                      Add Join Key
                    </Button>
                  </Space>
                </Card>
              </div>

              <Row gutter={24} className="mb-8">
                <Col xs={24} lg={12} className="mb-8 lg:mb-0">
                  <h3 className="text-purple-400 font-semibold mb-4">
                    3. SELECT COLUMNS (PRIMARY)
                  </h3>

                  <Card className="bg-[#020617] border border-gray-700 antd-transfer-dark-container overflow-x-auto">
                    <div className="min-w-[500px] w-full">
                      <Transfer
                        dataSource={file1Columns.map((col) => ({
                          key: col,
                          title: col,
                        }))}
                        titles={[<strong>Source</strong>, <strong>Target</strong>]}
                        targetKeys={selectedColumns1}
                        onChange={(keys) => dispatch(setSelectedColumns1(keys))}
                        render={(item) => item.title}
                        showSearch
                        filterOption={(inputValue, item) =>
                          item.title.toLowerCase().includes(inputValue.toLowerCase())
                        }
                        listStyle={{
                          height: 300,
                          width: "60%",
                          background: "#0f172a",
                          color: "white",
                          border: "1px solid #374151",
                        }}
                        operations={[">>", "<<"]}
                        className="custom-transfer"
                      />
                    </div>
                  </Card>
                </Col>

                <Col xs={24} lg={12}>
                  <h3 className="text-purple-400 font-semibold mb-4">
                    4. SELECT COLUMNS (SECONDARY)
                  </h3>

                  <Card className="bg-[#020617] border border-gray-700 antd-transfer-dark-container overflow-x-auto">
                    <div className="min-w-[500px] w-full">
                      <Transfer
                        dataSource={file2Columns.map((col) => ({
                          key: col,
                          title: col,
                        }))}
                        titles={[<strong>Source</strong>, <strong>Target</strong>]}
                        targetKeys={selectedColumns2}
                        onChange={(keys) => dispatch(setSelectedColumns2(keys))}
                        render={(item) => item.title}
                        showSearch
                        filterOption={(inputValue, item) =>
                          item.title.toLowerCase().includes(inputValue.toLowerCase())
                        }
                        listStyle={{
                          height: 300,
                          width: "60%",
                          background: "#0f172a",
                          color: "white",
                          border: "1px solid #374151",
                        }}
                        operations={[">>", "<<"]}
                        className="custom-transfer"
                      />
                    </div>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {activeTab === "append" && (
            <div className="mb-8">
              <h3 className="text-purple-400 font-semibold mb-4 tracking-wider text-lg sm:text-xl">
                SCHEMA ALIGNMENT & AUTO-DETECTION
              </h3>

              <Card className="bg-[#020617] border border-gray-700 rounded-xl">
                <p className="text-gray-400 mb-6 text-sm sm:text-base">
                  XynexAI has detected non-matching columns. These will be
                  automatically appended to the master schema.
                </p>

                {/* Table Header for larger screens */}
                <Row gutter={16} className="hidden lg:flex mb-4 text-gray-400 font-semibold">
                  <Col span={9}>Master Column (File 1)</Col>
                  <Col span={2} className="text-center">Maps To</Col>
                  <Col span={9}>Source Column (File 2)</Col>
                  <Col span={4} className="text-center">Status</Col>
                </Row>

                <div className="max-h-[500px] columnSelection-card overflow-y-auto pr-2">
                  <Space direction="vertical" className="w-full" size="middle">
                    {appendMapping.map((mapItem, idx) => {
                      const isMatched = mapItem.source;

                      return (
                        <Row
                          key={idx}
                          gutter={[16, 16]}
                          align="middle"
                          className="bg-[#0f172a] border border-gray-700 rounded-lg p-4"
                        >
                          {/* MASTER COLUMN */}
                          <Col xs={24} lg={9}>
                            <label className="lg:hidden text-gray-400 text-xs mb-1 block">Master Column</label>
                            <div className="bg-[#020617] border border-gray-600 rounded-md px-3 py-2 text-white font-mono text-sm truncate">
                              {mapItem.target || "(New Master Column)"}
                            </div>
                          </Col>

                          {/* ARROW */}
                          <Col xs={24} lg={2} className="text-center text-gray-300">
                            <div className="lg:hidden"><ArrowDownOutlined /></div>
                            <div className="hidden lg:block"><ArrowLeftOutlined /></div>
                          </Col>

                          {/* SOURCE SELECT */}
                          <Col xs={24} lg={9}>
                            <label className="lg:hidden text-gray-400 text-xs mb-1 block">Source Column</label>
                            <Select
                              value={mapItem.source}
                              onChange={(val) => {
                                const newMap = [...appendMapping];
                                newMap[idx] = { ...newMap[idx], source: val };
                                dispatch(setAppendMapping(newMap));
                              }}
                              className="w-full custom-select-dark" // antd Select is responsive by default
                              popupClassName="custom-select-dropdown-dark"
                              allowClear
                              placeholder="Select Source Column"
                              showSearch
                              filterOption={(input, option) =>
                                (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                            >
                              {file2Columns.map((c) => <Option key={c} value={c}>{c}</Option>)}
                            </Select>
                          </Col>

                          {/* STATUS */}
                          <Col xs={24} lg={4} className="flex justify-end lg:justify-center items-center">
                            {isMatched ? (
                              <span className="bg-green-900/50 text-green-300 border border-green-700 px-3 py-1 rounded-full text-xs font-medium">
                                Match
                              </span>
                            ) : (
                              <span className="bg-purple-900/50 text-purple-300 border border-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                                Auto-Add
                              </span>
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                  </Space>
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      {/* FOOTER */}

      {analysisComplete && (
        <div className="flex justify-center sm:justify-start items-center">
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-xs sm:max-w-none sm:w-auto">
            <Button
              className="bg-purple-600 !text-white hover:!bg-purple-700 transition-colors border-none px-6 h-10"
              icon={<SyncOutlined spin={merging} />}
              loading={merging}
              onClick={handleMerge}
              block
            >
              Execute Data Sync
            </Button>

            <Button
              icon={<DownloadOutlined />}
              className="bg-green-700 hover:!bg-green-800 transition-colors border-none !text-white px-6 h-10"
              hidden={!mergedFileUrl || merging}
              onClick={handleDownload}
              block
            >
              Download
            </Button>
            <Tooltip
              title={
                smartMergeMaxFileSize > planMaxUploadFileSize
                  ? `The merged file size of ${Math.round(smartMergeMaxFileSize)} MB is larger than your plan allows.`
                  : ""
              }
              overlayInnerStyle={{
                width: "300px",
                minHeight: "100px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center"
              }}
            >
              <span style={{ width: '100%', display: !blobFilePath || merging ? 'none' : 'block' }}>
                <Button
                  icon={<Cable />}
                  className="bg-purple-700 hover:!bg-purple-800 transition-colors border-none !text-white px-6 h-10 disabled:!bg-gray-600 disabled:!text-gray-400"
                  disabled={smartMergeMaxFileSize > planMaxUploadFileSize}
                  style={smartMergeMaxFileSize > planMaxUploadFileSize ? { pointerEvents: "none" } : {}}
                  onClick={connect_result_file_to_analytics}
                  block
                >
                  Connect Result File To Analytics
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartMergeDashboard;